import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../components/ui/Button';
import { Dialog } from '../../../components/ui/Dialog';
import { applyServerErrors, SelectField, TextField } from '../../../components/ui/Field';
import { ErrorState } from '../../../components/ui/Page';
import { useToast } from '../../../components/ui/Toast';
import { ApiError } from '../../../services/http';
import { useAuth } from '../../auth/AuthProvider';
import { teamApi, teamKeys } from '../api';

const schema = z.object({
  fullName: z.string().trim().min(1, 'Enter a name').max(150),
  email: z.string().trim().email('Enter a valid email'),
  phone: z.string().trim(),
  designation: z.string().trim().max(100),
  primaryBranchId: z.string(),
  roleId: z.string().min(1, 'Choose a role'),
});
type Values = z.infer<typeof schema>;

/** Invite a staff member with a role in a branch (FR-ORG-03). Only roles below the inviter's rank are offered. */
export function InviteStaffDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
}) {
  const toast = useToast();
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const roles = useQuery({ queryKey: teamKeys.roles, queryFn: teamApi.roles, enabled: open });
  const branches = useQuery({
    queryKey: teamKeys.branches,
    queryFn: () => teamApi.branches({ status: 'active' }),
    enabled: open,
  });
  const assignable = (roles.data ?? []).filter((r) => r.rank < (me?.rank ?? 0));
  const form = useForm<Values>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (open) {
      const sales = assignable.find((r) => r.code === 'sales_executive');
      form.reset({
        fullName: '',
        email: '',
        phone: '',
        designation: '',
        primaryBranchId: branches.data?.data[0]?.id ?? '',
        roleId: sales?.id ?? assignable[0]?.id ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roles.data, branches.data]);

  const invite = useMutation({
    mutationFn: (v: Values) =>
      teamApi.invite({
        fullName: v.fullName,
        email: v.email,
        phone: v.phone || null,
        designation: v.designation || null,
        primaryBranchId: v.primaryBranchId || null,
        roles: [{ roleId: v.roleId, branchId: v.primaryBranchId || null }],
      }),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ['team', 'staff'] });
      toast.success(
        res.inviteEmailSent
          ? `Invitation sent to ${res.email}`
          : `${res.fullName} was added, but the email could not be sent. Use "Resend invitation".`,
      );
      onOpenChange(false);
    },
    onError: (err) =>
      err instanceof ApiError ? applyServerErrors(err.fieldErrors, form.setError as never) : undefined,
  });
  const e = form.formState.errors;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Invite staff member"
      description="They receive an email to set their password. The link is valid for 7 days."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="invite-form" loading={invite.isPending}>
            Send invitation
          </Button>
        </>
      }
    >
      <form
        id="invite-form"
        noValidate
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={form.handleSubmit((v) => invite.mutate(v))}
      >
        {invite.error && !(invite.error instanceof ApiError && invite.error.code === 'VALIDATION_FAILED') ? (
          <div className="sm:col-span-2">
            <ErrorState error={invite.error} />
          </div>
        ) : null}
        <TextField label="Full name" required error={e.fullName?.message} {...form.register('fullName')} />
        <TextField
          label="Work email"
          type="email"
          required
          error={e.email?.message}
          {...form.register('email')}
        />
        <TextField label="Phone" type="tel" error={e.phone?.message} {...form.register('phone')} />
        <TextField label="Designation" error={e.designation?.message} {...form.register('designation')} />
        <SelectField
          label="Branch"
          placeholder="All branches"
          options={(branches.data?.data ?? []).map((b) => ({ value: b.id, label: b.name }))}
          {...form.register('primaryBranchId')}
        />
        <SelectField
          label="Role"
          required
          error={e.roleId?.message}
          options={assignable.map((r) => ({ value: r.id, label: r.name }))}
          {...form.register('roleId')}
        />
      </form>
    </Dialog>
  );
}
